import React from "react";
import Plot from "react-plotly.js";
import { chartAttrs, defaultConfig, defaultStyles, graphColors } from "models/core";
import { truncateStringWithEllipsis } from "helpers/stringHelper";
import { DashTable } from "./DashTable";
import { Color, PieData, PlotData } from "plotly.js";
import { UNKNOWN_TERMS } from "charts/core";
import { Person } from "@mui/icons-material";

export function CreateChart(height: number, 
                            width: number, 
                            paperHeight: number, 
                            chartOrientation: string, 
                            chartType: string,
                            showChart: boolean, 
                            xtitle: string, 
                            ytitle: string,
                            labels: any[],
                            values: any[],
                            traces: Partial<PlotData>[] | Partial<PieData>[], 
                            filterFn?: Function,
							styling?: Partial<Plotly.Layout>,
							debug?: boolean,
							maxValue?: number) {

/**
 * Component to create the JSX element of the chart that will be drawn inside the paper on the dash 
 */

	let chart: JSX.Element = null;
	let chartTraces = [];

	//Pie charts have slightly different options available so transform the traces to Pie Data 
	if (chartType == "pie") { 
		chartTraces = traces as Partial<PieData>[];
	} else {
		chartTraces = traces as Partial<PlotData>[];
	}

		//Define the basic layout styling that will be applied based on the chart type and orientation
		//Any changes to this for an individual chart can be sent in the chart file
		const style = defaultStyles.defaultBox;
		const bx = chartAttrs(style.height);
		bx.layout.autosize = true;
        bx.layout.hovermode = "closest";
        bx.layout.selectdirection = (chartOrientation =="v" ? "h" : "v");
		bx.layout.barmode = "group";
		bx.layout.margin = { l: 20, r: 20, b: 20, t: 20, pad: 2 };
		bx.layout.xaxis = { automargin:true,
			autotick: true, 
			dtick: 1, title: xtitle, 
			fixedrange: true,
			autotypenumbers: 'strict',
		};
		bx.layout.yaxis = { automargin:true,
			autotick: true, 
			dtick: 1, title: ytitle, 
			fixedrange: true,
			showticklabels: true,    
			autotypenumbers: 'strict',         
		};


		//Trim labels to avoid really long x axis labels 
		const trimmedLabels: string[] | number[] = (typeof labels[0] == "number" ? labels : labels.map(a => truncateStringWithEllipsis(a, 15)));

		if  (chartType == "pie") {
			bx.layout.margin = { l: 35, r: 35, b: 35, t: 45, pad: 2 };
		}

		if (chartOrientation == "h") {
			bx.layout.xaxis.range = [0, Math.max(...values) * (1 + ((window.innerWidth*-0.012+55)/100))] //We increase the range to prevent labels being cut off
			bx.layout.yaxis.autorange = true;
			bx.layout.yaxis.rangemode = 'tozero';
			bx.layout.yaxis.tickmode = "array";
			bx.layout.yaxis.ticktext = trimmedLabels;
			bx.layout.yaxis.tickfont = {size: 12};
			bx.layout.yaxis.tickvals = labels;
			bx.layout.yaxis.tickformat = ',d';
		}

		if (chartOrientation == "v") {
            bx.layout.yaxis.range = [0, Math.max(...values) *1.15]  //We increase the range to prevent labels being cut off
			bx.layout.xaxis.autorange = true;
			bx.layout.xaxis.rangemode = 'tozero';
			bx.layout.xaxis.tickmode = "array";
			bx.layout.xaxis.ticktext = trimmedLabels;
			bx.layout.xaxis.tickvals = labels;
			bx.layout.xaxis.tickfont = {size: 12};
			bx.layout.xaxis.tickformat = ',d';
		}

	//Here we are dictating the width of the bars to ensure they are always a decent thickness and cause a scroll chart rather than making the bars really 
	//thin to fit onto the chart dimensions provided
	if (chartType=="bar") {
		let multiplier = (styling?.barmode && styling.barmode == "overlay" ? 1 : chartTraces.length);
		if (chartOrientation=="v") {
			bx.layout.width = (chartTraces[0]?.x?.length == 1 ? width*0.5 : (width > chartTraces[0]?.x?.length*40*multiplier ? width : chartTraces[0]?.x?.length*40*multiplier));
			bx.layout.height = height;
		} else {
			bx.layout.height = (chartTraces[0]?.y?.length == 1 ? height*0.5 : (height > chartTraces[0]?.y?.length*40*multiplier ? height : chartTraces[0]?.y?.length*40*multiplier));
			bx.layout.width = width;
		}
	} else {
		bx.layout.height = height;
		bx.layout.width = width;
	}

	//if there is any specific styling for the chart, apply here
	if (styling) {
		bx.layout = Object.assign({}, bx.layout, styling);
	}

	//Make bar trace green for unknown 

	let barColor: Color[] = [];

	
	
	if (chartTraces.length == 1 && chartOrientation == "v" && chartType == "bar") {
		chartTraces.map((a, i) => {
			a?.x?.map(x => {
			return barColor.push(UNKNOWN_TERMS.indexOf(x) > -1 ? graphColors.unknown : graphColors.default[i]);
		})

		chartTraces[i].marker.color = barColor;
		})
	}

	if (chartTraces.length == 1 && chartOrientation == "h" && chartType == "bar") {
		chartTraces.map((a, i) => {
			a?.y?.map(y => {
			return barColor.push(UNKNOWN_TERMS.indexOf(y) > -1
				? graphColors.unknown : graphColors.default[i]);
		})

		chartTraces[i].marker.color = barColor;
		})
	}

	if (chartType == "pie") {
		chartTraces.map((a, i) => {
			a?.labels?.map((y, j) => {
			return barColor.push(UNKNOWN_TERMS.indexOf(y) > -1
				? graphColors.unknown : graphColors.default);
		})

		chartTraces[i].marker.colors = barColor;
		})
	}
	
	if (chartType == "person") {
		let personValues = chartTraces.find(a => a.y?.length > 0).y ?? [];	
		chart = (personValues.map((a, index) => { 
			return (
				<div key={labels[index]+ a} style={{display:"inline-block", width: "50%"}}>
					<Person key={labels[index]} 
							style={{height: "50%", width: "50%", color: "#1e88e5", opacity: ((a/personValues.reduce((x, y) => x + y, 0))*100+20)+"%"}}
					/>
					<br/>
					{labels[index]} 
					<br />
					{Math.round((a/personValues.reduce((x, y) => x + y, 0))*100)+"%"}
					<br/>
					{"("+personValues[index]+")"}
				</div>
			)
		}))
	}

	//Draw the plotly charts (anything but table currently - if we want to add pictograms etc they will need to be excluded here too and added below)
	if (showChart && chartType !== "table" && chartType !== "person") {
		chart = (
				<Plot
					data={chartTraces}
					layout={bx.layout}
					style={{overflowX: (chartOrientation == "v" ? "auto" : "hidden"), 
							overflowY: (chartOrientation == "h" ? "auto": "hidden"),
							height: "100%",
							maxHeight: height+"px",
							maxWidth: "1520px"}}
					config= {defaultConfig}
					onClick={filterFn}
					onSelected={filterFn}
				/>   
		);
	}

	//Draw any tables 
	if (showChart && chartType == "table") {
        chart = (
			<div>
                    <div style={{overflowY: "auto", 
                                overflowX: "auto", 
                                maxHeight: height+"px", 
                                cursor: "pointer",
                                marginLeft: "-20px",
                                marginRight: "-20px"}}>
                    <DashTable 
						traces={chartTraces}
						chartOrientation={chartOrientation}
						chartHeight={height + "px"}
						labelTitle={xtitle} 
                        valueTitle={ytitle}
						selectableFilter={false} 
						onChanged={filterFn}
                        />
                    </div>          
                </div>
		);
    }
	
	return chart;
	}